import type {TFunction} from 'i18next';
import {AutomationExecution, NodeType} from 'crqlar-react-common/dist/models/automation/automation';

/**
 * "Remind now" (features/automations.md in the API): a manual trigger in a flow that also has a survey
 * reminder trigger runs that trigger's reminders at once — a live run reminds every guest due (invited at
 * least a day ago, not answered, not yet reminded), a test run the newest one. The API answers such a run
 * with `remindNow`; when it queued nothing, the execution has no id.
 */
export interface RemindNowOutcome {
    /** Reminders queued (a test: 0 or 1). */
    reminders: number;
    /** More were due than one press queues; pressing again reminds the next batch. */
    more?: boolean;
}

/** The `remindNow` the trigger endpoint adds to its execution; absent for every other run. */
export const remindNowOutcome = (execution?: AutomationExecution | null): RemindNowOutcome | undefined =>
    (execution as (AutomationExecution & {remindNow?: RemindNowOutcome | null}) | null | undefined)?.remindNow ?? undefined;

/** Whether [triggerType], in a flow with these steps' types, reminds now rather than starting a run of its own. */
export const remindsNow = (triggerType: NodeType | string | undefined, flowTypes: (NodeType | string | undefined)[]): boolean =>
    triggerType === NodeType.MANUAL_TRIGGER && flowTypes.includes(NodeType.SURVEY_REMINDER_DUE);

/** The snackbar line after a live press: how many reminders went out, or that nobody was due. */
export const remindNowMessage = (outcome: RemindNowOutcome, t: TFunction): string => {
    if (outcome.reminders === 0) return t('Remind now nobody');
    const queued = t('Remind now queued', {count: outcome.reminders});
    return outcome.more ? `${queued} ${t('Remind now more left')}` : queued;
};
